import type { Options as ReactMarkdownOptions } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import type { Options as SanitizeSchema } from 'rehype-sanitize';

// Markdown shown in the admin UI can come from anonymous live-chat visitors or from bot replies
// that can be steered by prompt injection, and rehype-raw turns any HTML in it into real elements.
// rehype-sanitize therefore has to run AFTER rehype-raw.
//
// The schema is GitHub's default (no script/iframe/form/button/object/embed/style elements, no
// style/class/on* attributes, http(s)/mailto links only), with two restrictions on top:
// - `input` is removed (the default allows it only as a disabled GFM task-list checkbox);
// - `<style>` is stripped with its contents, so CSS does not leak into the message as text.
// Link target/rel come from the `components.a` overrides, which run after sanitisation.
const { input: _inputAttributes, ...attributes } = defaultSchema.attributes ?? {};
const { input: _inputRequired, ...required } = defaultSchema.required ?? {};

export const markdownSanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  tagNames: (defaultSchema.tagNames ?? []).filter((tagName) => tagName !== 'input'),
  attributes,
  required,
  strip: [...(defaultSchema.strip ?? []), 'style'],
};

export const markdownRehypePlugins: NonNullable<ReactMarkdownOptions['rehypePlugins']> = [
  rehypeRaw,
  [rehypeSanitize, markdownSanitizeSchema],
];

const SVG_TAG_NAMES = [
  'svg',
  'g',
  'path',
  'circle',
  'ellipse',
  'rect',
  'line',
  'polyline',
  'polygon',
  'text',
  'tspan',
];
const SVG_ATTRIBUTES = [
  'viewBox',
  'width',
  'height',
  'fill',
  'fillOpacity',
  'fillRule',
  'stroke',
  'strokeWidth',
  'strokeLineCap',
  'strokeLineJoin',
  'strokeOpacity',
  'opacity',
  'transform',
  'd',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'points',
  'textAnchor',
  'fontSize',
  'fontWeight',
  'fontFamily',
];

export const outputMarkdownSanitizeSchema: SanitizeSchema = {
  ...markdownSanitizeSchema,
  tagNames: [...(markdownSanitizeSchema.tagNames ?? []), ...SVG_TAG_NAMES],
  attributes: {
    ...attributes,
    '*': [...(attributes['*'] ?? []), 'style'],
    ...Object.fromEntries(SVG_TAG_NAMES.map((tagName) => [tagName, SVG_ATTRIBUTES])),
  },
};

export const outputMarkdownRehypePlugins: NonNullable<ReactMarkdownOptions['rehypePlugins']> = [
  rehypeRaw,
  [rehypeSanitize, outputMarkdownSanitizeSchema],
];
